import { useState, useMemo, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Info, Trash2, Plus, X, Boxes, RefreshCw, AlertTriangle, MessageSquare, Check } from "lucide-react"
import { adminModulesApi, type ModulePromptDTO } from "@/lib/api"
import type { Module, ModuleConfig } from "@/data/adminData"

type Props = {
  modules: Module[]
  onModulesChange: (m: Module[]) => void
}

const CATEGORY_COLORS: Record<string, string> = {
  "Generation": "bg-violet-500/10 text-violet-400",
  "Input":      "bg-blue-500/10 text-blue-400",
  "Processing": "bg-emerald-500/10 text-emerald-400",
  "Output":     "bg-amber-500/10 text-amber-400",
}
function categoryColor(cat: string) {
  return CATEGORY_COLORS[cat] ?? "bg-secondary text-muted-foreground"
}
function CategoryBadge({ category }: { category: string }) {
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${categoryColor(category)}`}>{category}</span>
}

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      title={enabled ? "Disable" : "Enable"}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${enabled ? "bg-primary" : "bg-muted"}`}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4.5" : "translate-x-0.5"}`} />
    </button>
  )
}

// ── Info + Config dialog ──────────────────────────────────────────────────────

function InfoDialog({
  module: m,
  onClose,
  onConfigSave,
}: {
  module: Module
  onClose: () => void
  onConfigSave: (config: ModuleConfig) => Promise<void>
}) {
  const serialize = (v: unknown): string => Array.isArray(v) ? v.join(", ") : String(v ?? "")
  const deserialize = (k: string, v: string): unknown =>
    k === 'prompt_types' || v.includes(',') ? v.split(',').map(s => s.trim()).filter(Boolean) : v

  const [entries, setEntries] = useState<[string, string][]>(() =>
    Object.entries(m.config).map(([k, v]) => [k, serialize(v)])
  )
  const [saving, setSaving] = useState(false)

  const add    = () => setEntries(prev => [...prev, ["", ""]])
  const remove = (i: number) => setEntries(prev => prev.filter((_, idx) => idx !== i))
  const setKey = (i: number, k: string) => setEntries(prev => prev.map((e, idx) => idx === i ? [k, e[1]] : e))
  const setVal = (i: number, v: string) => setEntries(prev => prev.map((e, idx) => idx === i ? [e[0], v] : e))

  const handleSave = async () => {
    setSaving(true)
    const config: ModuleConfig = Object.fromEntries(
      entries.filter(([k]) => k.trim()).map(([k, v]) => [k, String(deserialize(k, v))])
    )
    await onConfigSave(config)
    setSaving(false)
    onClose()
  }

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Module info</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">

          {/* Read-only metadata */}
          <div className="rounded-lg border bg-muted/20 px-4 py-3 flex flex-col gap-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">From file</div>
            <div className="grid grid-cols-[6rem_1fr] gap-y-1.5 text-sm">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{m.name}</span>
              <span className="text-muted-foreground">Category</span>
              <CategoryBadge category={m.category} />
              <span className="text-muted-foreground">Version</span>
              <span className="font-mono text-xs">v{m.version}</span>
              <span className="text-muted-foreground">Description</span>
              <span className="text-xs text-muted-foreground leading-snug">{m.description || "—"}</span>
              <span className="text-muted-foreground">File</span>
              <span className={`text-xs font-mono truncate ${m.file_path ? "text-emerald-400" : "text-amber-400"}`}>
                {m.file_path ? m.file_path.split("/").pop() : "⚠ missing"}
              </span>
            </div>
          </div>

          {/* Editable config */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Config — stored in DB</div>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs gap-1" onClick={add}>
                <Plus className="w-3 h-3" /> Add
              </Button>
            </div>
            <div className="flex flex-col gap-1.5">
              {entries.map(([k, v], i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input className="h-7 text-xs font-mono" placeholder="key" value={k} onChange={e => setKey(i, e.target.value)} />
                  <Input className="h-7 text-xs font-mono" placeholder={k === 'prompt_types' ? "optique, solaire" : "value"} value={v} onChange={e => setVal(i, e.target.value)} />
                  <Button size="icon" variant="ghost" className="w-6 h-6 text-muted-foreground hover:text-destructive flex-shrink-0" onClick={() => remove(i)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {entries.length === 0 && (
                <div className="text-xs text-muted-foreground py-1">No parameters.</div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={handleSave} disabled={saving}>Save config</Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Prompts manager dialog ────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars

function PromptsDialog({ module: m, onClose }: { module: Module; onClose: () => void }) {
  const [prompts, setPrompts] = useState<ModulePromptDTO[]>([])
  const [loading, setLoading] = useState(true)
  const promptTypes: string[] = Array.isArray(m.config?.prompt_types) ? m.config.prompt_types as string[] : []
  const isDualType = promptTypes.length > 0

  useEffect(() => {
    adminModulesApi.prompts.list(m.id).then(data => { setPrompts(data); setLoading(false) }).catch(() => setLoading(false))
  }, [m.id])

  // single-prompt state
  const [viewTarget, setViewTarget] = useState<ModulePromptDTO | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newContent, setNewContent] = useState("")
  const [newLabel, setNewLabel] = useState("")

  const handleActivate = async (promptId: number) => {
    try { const updated = await adminModulesApi.prompts.activate(m.id, promptId); setPrompts(updated) }
    catch { setPrompts(prev => prev.map(p => ({ ...p, is_active: p.id === promptId }))) }
  }
  const handleDelete = async (promptId: number) => {
    try { await adminModulesApi.prompts.remove(m.id, promptId) } catch { /* ignore */ }
    setPrompts(prev => prev.filter(p => p.id !== promptId))
  }
  const handleCreate = async () => {
    if (!newContent.trim()) return
    try { const c = await adminModulesApi.prompts.create(m.id, { content: newContent, label: newLabel }); setPrompts(prev => [c, ...prev]) }
    catch { setPrompts(prev => [{ id: Date.now(), module_id: m.id, version: prev.length + 1, label: newLabel, content: newContent, is_active: prev.length === 0, created_at: new Date().toISOString() }, ...prev]) }
    setNewContent(""); setNewLabel(""); setShowCreate(false)
  }

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent className="max-w-xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-4 h-4" />Prompts — {m.name}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto flex flex-col gap-4 pr-1">
          {loading && <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>}
          {!loading && prompts.length === 0 && !showCreate && (
            <div className="text-xs text-muted-foreground py-4 text-center">No prompts yet.</div>
          )}
          {!loading && viewTarget && (
            <div className="rounded-lg border p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold text-muted-foreground">{viewTarget.label ? viewTarget.label : `v${viewTarget.version}`}</span>
                <button className="text-xs text-muted-foreground" onClick={() => setViewTarget(null)}>← Back</button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">{viewTarget.content}</pre>
            </div>
          )}
          {!loading && !viewTarget && prompts.map(p => (
            <div key={p.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${p.is_active ? "border-primary/30 bg-primary/5" : "border-border"}`}>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                {p.label && <span className="text-xs font-medium">{p.label}</span>}
                {p.is_active && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-primary"><Check className="w-3 h-3" /> active</span>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setViewTarget(p)}>View</Button>
                {!p.is_active && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-primary" onClick={() => handleActivate(p.id)}>Activate</Button>}
                <Button size="icon" variant="ghost" className="w-6 h-6 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(p.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          {!loading && !viewTarget && (showCreate ? (
            <div className="rounded-lg border p-3 flex flex-col gap-2">
              <Input className="h-7 text-xs" placeholder={isDualType ? `Label (${promptTypes.join(', ')})` : "Label"} value={newLabel} onChange={e => setNewLabel(e.target.value)} />
              <textarea className="w-full rounded-md border bg-background px-3 py-2 text-xs font-mono resize-none focus:outline-none focus:ring-1 focus:ring-ring" rows={6} placeholder="Prompt…" value={newContent} onChange={e => setNewContent(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={handleCreate} disabled={!newContent.trim()}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setNewContent(""); setNewLabel("") }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="gap-1.5 self-start" onClick={() => setShowCreate(true)}><Plus className="w-3.5 h-3.5" /> New prompt</Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ModulesPage({ modules, onModulesChange }: Props) {
  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("All")
  const [infoTarget, setInfoTarget] = useState<Module | null>(null)
  const [promptsTarget, setPromptsTarget] = useState<Module | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Module | null>(null)
  const [reloading, setReloading] = useState(false)

  const categories = useMemo(() => {
    const cats = [...new Set(modules.map(m => m.category))].sort()
    return ["All", ...cats]
  }, [modules])

  const safeFilter = categories.includes(categoryFilter) ? categoryFilter : "All"

  const handleReload = async () => {
    setReloading(true)
    try {
      const updated = await adminModulesApi.reload()
      onModulesChange(updated)
    } catch { /* offline */ }
    setReloading(false)
  }

  const handleToggle = async (m: Module, enabled: boolean) => {
    try { await adminModulesApi.update(m.id, { enabled }) } catch { /* optimistic */ }
    onModulesChange(modules.map(x => x.id === m.id ? { ...x, enabled } : x))
  }

  const handleConfigSave = async (m: Module, config: ModuleConfig) => {
    try { await adminModulesApi.update(m.id, { config }) } catch { /* optimistic */ }
    onModulesChange(modules.map(x => x.id === m.id ? { ...x, config } : x))
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try { await adminModulesApi.remove(deleteTarget.id) } catch { /* optimistic */ }
    onModulesChange(modules.filter(x => x.id !== deleteTarget.id))
    setDeleteTarget(null)
  }

  const visible = modules.filter(m => {
    const matchCat = safeFilter === "All" || m.category === safeFilter
    const q = search.toLowerCase()
    return (safeFilter === "All" || matchCat) &&
      (!q || m.name.toLowerCase().includes(q) || m.description.toLowerCase().includes(q) || m.category.toLowerCase().includes(q))
  })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Modules</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Drop a <code className="text-xs bg-muted px-1 py-0.5 rounded">*.py</code> in{" "}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">backend/modules/</code> then click Reload
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={handleReload} disabled={reloading}>
          <RefreshCw className={`w-3.5 h-3.5 ${reloading ? "animate-spin" : ""}`} />
          Reload
        </Button>
      </div>

      {modules.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Input className="w-56 h-8 text-sm" placeholder="Search modules…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="flex gap-1 flex-wrap">
            {categories.map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${safeFilter === c ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {modules.length === 0 ? (
          <div className="py-12 px-6 flex flex-col items-center gap-3 text-center">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <Boxes className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <div className="text-sm font-medium">No modules detected</div>
              <div className="text-xs text-muted-foreground mt-1">
                Create a <code className="bg-muted px-1 rounded">.py</code> file in{" "}
                <code className="bg-muted px-1 rounded">backend/modules/</code> with a{" "}
                <code className="bg-muted px-1 rounded">MODULE_META</code> dict, then click Reload.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 px-4 py-2 border-b bg-muted/30">
              <div className="w-9 flex-shrink-0" />
              <div className="flex-1 text-xs text-muted-foreground font-medium">Module</div>
              <div className="w-14 text-xs text-muted-foreground font-medium text-right flex-shrink-0">Version</div>
              <div className="w-9 text-xs text-muted-foreground font-medium text-center flex-shrink-0">On</div>
              <div className="w-28 flex-shrink-0" />
            </div>
            {visible.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">No modules match this filter.</div>
            )}
            {visible.map(m => (
              <div key={m.id} className={`flex items-center gap-4 px-4 py-3 border-b last:border-0 ${!m.file_path ? "opacity-60" : ""}`}>
                <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <Boxes className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{m.name}</span>
                    <CategoryBadge category={m.category} />
                    {!m.file_path && (
                      <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> file missing
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{m.description}</div>
                </div>
                <span className="text-xs text-muted-foreground w-14 text-right flex-shrink-0">v{m.version}</span>
                <div className="w-9 flex items-center justify-center flex-shrink-0">
                  <Toggle enabled={m.enabled} onChange={v => handleToggle(m, v)} />
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-foreground" title="Info & config" onClick={() => setInfoTarget(m)}>
                    <Info className="w-3.5 h-3.5" />
                  </Button>
                  {m.has_prompt && (
                    <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-foreground" title="Prompts" onClick={() => setPromptsTarget(m)}>
                      <MessageSquare className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground hover:text-destructive" title="Delete" onClick={() => setDeleteTarget(m)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}
      </Card>

      {infoTarget && (
        <InfoDialog
          module={infoTarget}
          onClose={() => setInfoTarget(null)}
          onConfigSave={async (config) => {
            await handleConfigSave(infoTarget, config)
          }}
        />
      )}

      {promptsTarget && (
        <PromptsDialog
          module={promptsTarget}
          onClose={() => setPromptsTarget(null)}
        />
      )}

      <Dialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete module</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-4 pt-1">
            <p className="text-sm text-muted-foreground">
              Remove <span className="text-foreground font-medium">{deleteTarget?.name}</span> from the registry?
              The <code className="text-xs bg-muted px-1 rounded">.py</code> file on disk is not deleted.
            </p>
            <div className="flex gap-2">
              <Button variant="destructive" className="flex-1" onClick={handleDelete}>Remove</Button>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
